import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { api, type KillGrowthClass, type VsPowerRow, type VsPowerWeeks } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatClassRange, KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { Loading, Panel, Segmented, Th, fmtM, playerLabel } from './shared';

// Plan 052 — VS points relative to strength, compared within power classes.

const CLASS_ORDER: KillGrowthClass[] = ['rising', 'mid', 'top'];

export default function VsPowerTab() {
  const [weeks, setWeeks] = useState<VsPowerWeeks>(4);
  const [filter, setFilter] = useState<KillGrowthClass | 'all'>('all');
  const q = useQuery({
    queryKey: ['insights', 'vs-power', weeks],
    queryFn: () => api.getAdminVsPower(weeks),
  });
  const rows = useMemo(
    () => (q.data?.players ?? []).filter((r) => filter === 'all' || r.class === filter),
    [q.data, filter],
  );

  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          Weekly VS points per M of M-Sum (at the week's start), median over the last{' '}
          {data.weeks} completed weeks. Index 100 = class median. Weeks on leave are
          skipped; players need {data.minWeeks}+ weeks.
          {data.notComparable > 0 && ` ${data.notComparable} not comparable.`}
        </p>
        <Segmented
          value={String(weeks) as '4' | '12'}
          onChange={(v) => setWeeks(Number(v) as VsPowerWeeks)}
          options={[
            { value: '4', label: '4 weeks' },
            { value: '12', label: '12 weeks' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {CLASS_ORDER.map((key) => {
          const c = data.classes.find((x) => x.key === key)!;
          const meta = KILL_GROWTH_CLASS_META[key];
          return (
            <div key={key} className={cn('rounded-lg border px-3 py-2', meta.chip)}>
              <div className="text-[10px] font-semibold uppercase tracking-wider">{meta.label}</div>
              {formatClassRange(c.minMSum, c.maxMSum) && (
                <div className="text-[10px] tabular-nums text-slate-400">
                  M-Sum {formatClassRange(c.minMSum, c.maxMSum)}
                </div>
              )}
              <div className="text-xl font-semibold tabular-nums text-slate-100">
                {fmtM(c.medianRatio)} <span className="text-xs font-normal text-slate-400">VS / M</span>
              </div>
              <div className="text-[11px] text-slate-400">
                median VS {fmtM(c.medianVs, 0)} M · {c.players} players
              </div>
            </div>
          );
        })}
      </div>

      <Panel title="VS vs. strength">
        <VsScatter rows={data.players} />
      </Panel>

      <Panel
        title="Players"
        actions={
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All' },
              ...CLASS_ORDER.map((k) => ({ value: k, label: KILL_GROWTH_CLASS_META[k].label })),
            ]}
          />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <Th left>Player</Th>
                <Th left>Class</Th>
                <Th>Index</Th>
                <Th>VS / M</Th>
                <Th>VS median</Th>
                <Th>M-Sum</Th>
                <Th>Class rank</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = KILL_GROWTH_CLASS_META[r.class];
                return (
                  <tr key={r.player.id} className="border-t border-slate-800/60">
                    <td className="px-2 py-1">{playerLabel(r.player)}</td>
                    <td className="px-2 py-1 text-xs" style={{ color: meta.color }}>
                      {meta.label}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-1 text-right font-semibold tabular-nums',
                        r.index != null && r.index >= 100 ? 'text-emerald-300' : 'text-amber-300',
                      )}
                    >
                      {r.index == null ? '—' : Math.round(r.index)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtM(r.ratio)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                      {fmtM(r.vsMedian, 0)} M
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">
                      {fmtM(r.mSum, 1)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">
                      {r.classRank} / {r.classSize}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function VsScatter({ rows }: { rows: VsPowerRow[] }) {
  const series = CLASS_ORDER.map((key) => ({
    key,
    meta: KILL_GROWTH_CLASS_META[key],
    points: rows
      .filter((r) => r.class === key)
      .map((r) => ({ x: r.mSum, y: r.vsMedian, name: playerLabel(r.player), index: r.index })),
  }));
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 12, bottom: 16, left: 0 }}>
          <CartesianGrid stroke="#1e293b" />
          <XAxis
            type="number"
            dataKey="x"
            name="M-Sum"
            domain={['dataMin - 5', 'dataMax + 5']}
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            tickFormatter={(v: number) => v.toFixed(0)}
            label={{ value: 'M-Sum', position: 'insideBottom', offset: -8, fill: '#64748b', fontSize: 11 }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="VS"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            width={40}
          />
          <ZAxis range={[40, 40]} />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ payload }) => {
              const p = payload?.[0]?.payload as
                | { name: string; x: number; y: number; index: number | null }
                | undefined;
              if (!p) return null;
              return (
                <div className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs">
                  <div className="font-medium">{p.name}</div>
                  <div className="tabular-nums text-slate-400">
                    M-Sum {p.x.toFixed(1)} · VS {p.y.toFixed(0)} M
                    {p.index != null && ` · index ${Math.round(p.index)}`}
                  </div>
                </div>
              );
            }}
          />
          {series.map((s) => (
            <Scatter key={s.key} name={s.meta.label} data={s.points} fill={s.meta.color} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
